import { useEffect } from "react";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useNavigationManagement } from "./hooks/use-navigation";
import { NotificationActionListener } from "./components/notifications/NotificationActionListener";
import Index from "./pages/Index";
import CaseDetail from "./pages/CaseDetail";
import NotFound from "./pages/NotFound";
import ReportGrievance from "./pages/ReportGrievance";
import AddPhoto from "./pages/AddPhoto";
import AddCase from "./pages/AddCase";
import CaseUpdates from "./pages/CaseUpdates";
import Notifications from "./pages/Notifications";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AuthLoader from "./AuthLoader";
import { initFCM } from "./fcm";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppRoutes = () => {
  useNavigationManagement();
  return (
    <>
      <NotificationActionListener />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/case/:id" element={<CaseDetail />} />
        <Route path="/report" element={<ReportGrievance />} />
        <Route path="/add-photo/:id?" element={<AddPhoto />} />
        <Route path="/add-case" element={<AddCase />} />
        <Route path="/case/:id/updates" element={<CaseUpdates />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/notifications/" element={<Navigate to="/notifications" replace />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => {
  // Init FCM on mount (native only). Works for both anonymous and logged-in users.
  useEffect(() => {
    initFCM();
  }, []);

  // Re-init FCM when auth token appears so backend can associate FCM token with user.
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      if (store.getState().auth?.token) {
        initFCM();
        unsubscribe();
      }
    });
    return unsubscribe;
  }, []);

  return (
    <Provider store={store}>
      <AuthLoader />
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
